import axios, { AxiosInstance as AxiosInstanceType } from "axios";
import {
  getAccessTokenInfo,
  getRefreshTokenInfo,
  saveAccessTokenInfo,
  saveRefreshTokenInfo,
} from "../data/user";
import { base_url } from "../constants";

interface RefreshTokenResponse {
  accesstoken: string;
  refreshtoken: string;
}

const baseURL = `${base_url}`;

const AxiosInstance: AxiosInstanceType = axios.create({
  baseURL,
  timeout: 20000,
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRrefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

AxiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getAccessTokenInfo();
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

AxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the refresh to complete and retry the failed request
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            resolve(AxiosInstance(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = await getRefreshTokenInfo();
        if (!refreshToken) throw new Error("No refresh token found");

        const resp = await refreshTokenCall(refreshToken);
        const data: RefreshTokenResponse = resp.data;

        saveAccessTokenInfo(data.accesstoken);
        saveRefreshTokenInfo(data.refreshtoken);

        onRrefreshed(data.accesstoken);
        isRefreshing = false;

        originalRequest.headers["Authorization"] = `Bearer ${data.accesstoken}`;
        return AxiosInstance(originalRequest);
      } catch (err) {
        isRefreshing = false;
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

const refreshTokenCall = async (refreshToken: string) => {
  const body = { token: refreshToken };

  return axios.post<RefreshTokenResponse>(
    `${baseURL}api/v1/object/refresh-token`,
    body,
    {
      headers: { "Content-Type": "application/json" },
      timeout: 10000,
    }
  );
};

export default AxiosInstance;
